import { useEffect, useState, useCallback } from 'react';
import API from '../../api';
import { useToast } from '../../context/ToastContext';

const STATUT_LABELS = { en_attente: 'En attente', en_cours: 'En cours', termine: 'Terminé' };
const STATUT_CLASSES = { en_attente: 'badge-warning', en_cours: 'badge-success', termine: 'badge-muted' };
const TYPE_ICONS = { pdf: 'bi-file-earmark-pdf', audio: 'bi-file-earmark-music', video: 'bi-file-earmark-play', autre: 'bi-file-earmark' };

export default function EtudiantCours() {
  const [cours, setCours] = useState([]);
  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [supports, setSupports] = useState({});
  const [activeMedia, setActiveMedia] = useState(null); // { supportId, type }
  const { addToast } = useToast();

  const loadCours = useCallback(() => {
    const url = statutFilter ? `cours/?statut=${statutFilter}` : 'cours/';
    API.get(url).then((r) => setCours(r.data.results || r.data));
  }, [statutFilter]);

  useEffect(() => { loadCours(); }, [loadCours]);

  const filtered = cours.filter((c) =>
    c.titre.toLowerCase().includes(search.toLowerCase()) ||
    (c.enseignant_nom || '').toLowerCase().includes(search.toLowerCase())
  );

  const participer = async (coursId) => {
    try {
      await API.post(`cours/${coursId}/participer/`);
      addToast('Vous participez au cours !', 'success');
      loadCours();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Erreur', 'error');
    }
  };

  const toggleSupports = async (coursId) => {
    if (supports[coursId]) {
      setSupports((prev) => { const n = { ...prev }; delete n[coursId]; return n; });
      setActiveMedia(null);
    } else {
      const r = await API.get(`supports/?cours=${coursId}`);
      setSupports((prev) => ({ ...prev, [coursId]: r.data.results || r.data }));
    }
  };

  const downloadSupport = (supportId, titre, typeSupport) => {
    API.get(`supports/${supportId}/download/`, { responseType: 'blob' })
      .then((res) => {
        const contentDisp = res.headers['content-disposition'] || '';
        const match = contentDisp.match(/filename="?([^"]+)"?/);
        let filename = match ? match[1] : null;
        if (!filename) {
          const extMap = { pdf: '.pdf', audio: '.mp3', video: '.mp4' };
          const ext = extMap[typeSupport] || '';
          filename = (titre || 'support') + ext;
        }
        const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => addToast('Erreur lors du téléchargement', 'error'));
  };

  const toggleMedia = (support) => {
    if (activeMedia?.supportId === support.id) {
      setActiveMedia(null);
    } else {
      setActiveMedia({ supportId: support.id, type: support.type_support, url: support.fichier_url, titre: support.titre });
    }
  };

  const renderMediaPlayer = (support) => {
    if (activeMedia?.supportId !== support.id) return null;
    const url = support.fichier_url;
    if (!url) return <p className="text-muted">Fichier non disponible</p>;

    const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
    const authUrl = `${url}${url.includes('?') ? '&' : '?'}token=${tokens.access || ''}`;

    switch (support.type_support) {
      case 'video':
        return (
          <div className="media-player-container">
            <video controls className="media-player-video" preload="metadata">
              <source src={authUrl} type="video/mp4" />
              Votre navigateur ne supporte pas la lecture vidéo.
            </video>
          </div>
        );
      case 'audio':
        return (
          <div className="media-player-container">
            <audio controls className="media-player-audio" preload="metadata">
              <source src={authUrl} type="audio/mpeg" />
              Votre navigateur ne supporte pas la lecture audio.
            </audio>
          </div>
        );
      case 'pdf':
        return (
          <div className="media-player-container">
            <iframe src={authUrl} className="media-player-pdf" title={support.titre}>
              <p>Impossible d'afficher le PDF. <button className="btn btn-sm btn-primary" onClick={() => downloadSupport(support.id, support.titre)}>Télécharger</button></p>
            </iframe>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2><i className="bi bi-book" /> Mes cours</h2>
      </div>

      <div className="filter-bar">
        <input
          type="text" placeholder="Rechercher un cours ou enseignant…"
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
        <select className="select-filter" value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}>
          <option value="">Tous les statuts</option>
          <option value="en_attente">En attente</option>
          <option value="en_cours">En cours</option>
          <option value="termine">Terminé</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state"><i className="bi bi-book" /><p>Aucun cours trouvé</p></div>
      ) : (
        <div className="cards-grid">
          {filtered.map((c) => (
            <div key={c.id} className="course-card">
              <div className="course-card-header">
                <h3>{c.titre}</h3>
                <span className={`badge-status ${STATUT_CLASSES[c.statut] || ''}`}>{STATUT_LABELS[c.statut] || c.statut}</span>
              </div>
              <p>{c.description}</p>
              <div className="card-meta">
                <span><i className="bi bi-person" /> {c.enseignant_nom}</span>
                {c.faculte_nom && <span><i className="bi bi-building" /> {c.faculte_nom}</span>}
                {c.promotion_label && <span><i className="bi bi-mortarboard" /> {c.promotion_label}</span>}
                {c.annee_academique && <span><i className="bi bi-calendar-range" /> {c.annee_academique}</span>}
                {c.date_debut && <span><i className="bi bi-calendar-event" /> {c.date_debut} → {c.date_fin || '...'}</span>}
              </div>
              <div className="card-actions">
                {c.est_inscrit ? (
                  <button className="btn btn-sm btn-secondary" onClick={() => toggleSupports(c.id)}>
                    <i className={supports[c.id] ? 'bi bi-eye-slash' : 'bi bi-folder2-open'} /> {supports[c.id] ? 'Masquer' : 'Supports'}
                  </button>
                ) : (
                  <button className="btn btn-sm btn-primary" onClick={() => participer(c.id)}>
                    <i className="bi bi-box-arrow-in-right" /> Participer
                  </button>
                )}
                {c.est_inscrit && <span className="badge-status badge-success" style={{marginLeft: 8}}><i className="bi bi-check-circle" /> Inscrit</span>}
              </div>
              {supports[c.id] && (
                <div className="section-expand">
                  {supports[c.id].length === 0 ? <p className="text-muted">Aucun support disponible.</p> : (
                    <div className="supports-list">
                      {supports[c.id].map((s) => (
                        <div key={s.id} className="support-item-wrapper">
                          <div className="support-item">
                            <span>
                              <i className={`bi ${TYPE_ICONS[s.type_support] || 'bi-file-earmark'}`} /> {s.titre}
                              <span className="badge-type">{s.type_support}</span>
                              {s.taille_display && <span className="support-meta">{s.taille_display}</span>}
                            </span>
                            <div className="support-actions">
                              {['video', 'audio', 'pdf'].includes(s.type_support) && (
                                <button className={`btn btn-sm ${activeMedia?.supportId === s.id ? 'btn-warning' : 'btn-secondary'}`}
                                  onClick={() => toggleMedia(s)} title={activeMedia?.supportId === s.id ? 'Fermer' : 'Lire'}>
                                  <i className={`bi ${activeMedia?.supportId === s.id ? 'bi-x-lg' : s.type_support === 'pdf' ? 'bi-eye' : 'bi-play-circle'}`} />
                                </button>
                              )}
                              <button className="btn btn-sm btn-primary" onClick={() => downloadSupport(s.id, s.titre, s.type_support)} title="Télécharger">
                                <i className="bi bi-download" />
                              </button>
                            </div>
                          </div>
                          {renderMediaPlayer(s)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
